/**
 * ActivityAnomalies — manager view of self vs auto activity deltas.
 *
 * Pulls /api/admin/activity-anomalies?days=14 and shows a sortable table.
 * Role-gated server-side; the AuthGuard in App.tsx already restricts the
 * route to manager + executive.
 */

import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { tokens } from "@/components/operator/tokens";
import { api } from "@/lib/session";

interface AnomalyRow {
  user_id: string;
  rep_name: string;
  rep_role: string;
  date: string;
  metric: string;
  metric_label: string;
  self: number;
  auto: number;
  delta: number;
  delta_pct: number | null;
  flagged: boolean;
  note: string;
  offline_note: string;
}

interface AnomalyResponse {
  days: number;
  from: string;
  to: string;
  tenant_id: number;
  rows: AnomalyRow[];
}

type SortKey = "delta" | "date" | "rep" | "metric";
type SortDir = "asc" | "desc";

const PRESETS = [
  { days: 7, label: "7 days" },
  { days: 14, label: "14 days" },
  { days: 30, label: "30 days" },
];

export default function ActivityAnomalies() {
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnomalyResponse | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("delta");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api<AnomalyResponse>(`/api/admin/activity-anomalies?days=${days}`);
        if (!cancelled) setData(r);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Failed to load activity audit");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days]);

  const rows = useMemo(() => {
    if (!data) return [] as AnomalyRow[];
    const base = showFlaggedOnly ? data.rows.filter((r) => r.flagged) : data.rows;
    const sorted = [...base].sort((a, b) => {
      const sign = sortDir === "desc" ? -1 : 1;
      switch (sortKey) {
        case "delta":
          return sign * (Math.abs(b.delta) - Math.abs(a.delta));
        case "date":
          return sign * b.date.localeCompare(a.date);
        case "rep":
          return sign * (b.rep_name ?? "").localeCompare(a.rep_name ?? "");
        case "metric":
          return sign * (b.metric_label ?? "").localeCompare(a.metric_label ?? "");
        default:
          return 0;
      }
    });
    return sorted;
  }, [data, sortKey, sortDir, showFlaggedOnly]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Admin", href: "/admin" },
        { label: "Activity Audit" },
      ]}
      pageTitle="Activity Audit"
      pageMeta={
        data ? `${data.from} → ${data.to} · ${rows.length} row${rows.length === 1 ? "" : "s"}` : "Loading…"
      }
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          {PRESETS.map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => setDays(p.days)}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                border: `1px solid ${tokens.color.border}`,
                background: p.days === days ? tokens.color.charcoal : tokens.color.canvas,
                color: p.days === days ? tokens.color.softWhite : tokens.color.textPrimary,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: tokens.font.body,
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: tokens.color.textPrimary,
            fontFamily: tokens.font.body,
          }}
        >
          <input
            type="checkbox"
            checked={showFlaggedOnly}
            onChange={(e) => setShowFlaggedOnly(e.target.checked)}
          />
          Flagged only (Δ over 20%)
        </label>
      </div>

      {loading && (
        <div
          style={{
            padding: 24,
            color: tokens.color.textMuted,
            fontFamily: tokens.font.body,
            fontSize: 13,
          }}
        >
          Loading anomalies…
        </div>
      )}

      {error && (
        <div
          style={{
            padding: 12,
            color: tokens.color.danger,
            background: tokens.color.dangerBg,
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <div
          style={{
            background: tokens.color.canvas,
            border: `1px solid ${tokens.color.border}`,
            borderRadius: tokens.radius.card,
            overflowX: "auto",
            boxShadow: tokens.shadow.card,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              fontSize: 13,
              fontFamily: tokens.font.body,
            }}
          >
            <thead>
              <tr style={{ background: tokens.color.surfaceMute }}>
                <SortableTh active={sortKey === "rep"} dir={sortDir} onClick={() => toggleSort("rep")}>
                  Rep
                </SortableTh>
                <SortableTh active={sortKey === "date"} dir={sortDir} onClick={() => toggleSort("date")}>
                  Date
                </SortableTh>
                <SortableTh
                  active={sortKey === "metric"}
                  dir={sortDir}
                  onClick={() => toggleSort("metric")}
                >
                  Metric
                </SortableTh>
                <Th align="right">Self</Th>
                <Th align="right">Auto</Th>
                <SortableTh
                  active={sortKey === "delta"}
                  dir={sortDir}
                  onClick={() => toggleSort("delta")}
                  align="right"
                >
                  Δ
                </SortableTh>
                <Th>Offline note</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      padding: 24,
                      textAlign: "center",
                      color: tokens.color.textMuted,
                      fontSize: 13,
                    }}
                  >
                    No activity rows in window. Reps may not have logged or connected yet.
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <tr
                  key={`${r.user_id}-${r.date}-${r.metric}-${i}`}
                  style={{
                    borderTop: `1px solid ${tokens.color.borderSoft}`,
                    background: r.flagged ? tokens.color.amberBg : undefined,
                  }}
                >
                  <Td>
                    <div style={{ fontWeight: 600 }}>{r.rep_name || "(unknown)"}</div>
                    <div style={{ fontSize: 11, color: tokens.color.textMuted }}>
                      {r.rep_role}
                    </div>
                  </Td>
                  <Td>{r.date}</Td>
                  <Td>{r.metric_label}</Td>
                  <Td align="right">{r.self}</Td>
                  <Td align="right">{r.auto}</Td>
                  <Td align="right">
                    <span
                      style={{
                        fontWeight: 700,
                        color: r.flagged ? tokens.color.amber : tokens.color.textPrimary,
                      }}
                    >
                      {r.delta > 0 ? `+${r.delta}` : r.delta}
                      {r.delta_pct !== null && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 11,
                            color: tokens.color.textMuted,
                            fontWeight: 500,
                          }}
                        >
                          {(r.delta_pct * 100).toFixed(0)}%
                        </span>
                      )}
                    </span>
                  </Td>
                  <Td>
                    <span style={{ fontSize: 12 }}>{r.offline_note || r.note || "—"}</span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </OperatorShell>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      style={{
        padding: "10px 12px",
        textAlign: align,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: tokens.color.textMuted,
        fontWeight: 700,
      }}
    >
      {children}
    </th>
  );
}

function SortableTh({
  children,
  active,
  dir,
  onClick,
  align = "left",
}: {
  children: React.ReactNode;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: "left" | "right";
}) {
  return (
    <th
      onClick={onClick}
      style={{
        padding: "10px 12px",
        textAlign: align,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: active ? tokens.color.textPrimary : tokens.color.textMuted,
        fontWeight: 700,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {children}
      {active && (
        <span style={{ marginLeft: 4, fontSize: 10 }}>{dir === "asc" ? "▲" : "▼"}</span>
      )}
    </th>
  );
}

function Td({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <td
      style={{
        padding: "10px 12px",
        textAlign: align,
        verticalAlign: "middle",
        color: tokens.color.textPrimary,
      }}
    >
      {children}
    </td>
  );
}
